import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Fade from "react-reveal/Fade";
import Button from "../Button";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";

function Main() {
  const { t } = useTranslation();

  const rows = useMemo(
    () => [
      {
        labelKey: "account.7",
        values: ["account.8", "account.9", "account.10", "account.11"],
      },
      {
        labelKey: "account.12",
        values: ["account.13", "account.14", "account.15", "account.16"],
      },
      {
        labelKey: "account.17",
        values: ["account.18", "account.19", "account.20", "account.21"],
      },
      {
        labelKey: "account.22",
        values: ["account.23", "account.24", "account.25", "account.26"],
      },
      {
        labelKey: "account.27",
        values: ["account.28", "account.29", "account.30", "account.31"],
      },
      {
        labelKey: "account.32",
        values: ["account.33", "account.34", "account.35", "account.36"],
      },
      {
        labelKey: "account.37",
        values: ["account.38", "account.39", "account.40", "account.41"],
      },
    ],
    []
  );

  const plans = useMemo(
    () => [
      { titleKey: "account.3", theme: "accountTheme.beginner" },
      { titleKey: "account.4", theme: "accountTheme.standard" },
      { titleKey: "account.5", theme: "accountTheme.pro" },
      { titleKey: "account.6", theme: "accountTheme.premium" },
    ],
    []
  );

  return (
    <div className="relative overflow-hidden mt-[60px]">
      <div className="max-w-[1220px] xl:mx-auto mx-[20px]">
        <Fade bottom>
          <div className="text-center">
            <h2 className="mt-3 text-[32px] xl:text-[48px] font-[500] poppins text-[#061A77]">
              {t("account.1")}
            </h2>
            <p className="mt-3 max-w-[636px] mx-auto text-[#000000B2] inter text-[14px]">
              {t("account.2")}
            </p>
          </div>

          {/* DESKTOP TABLE */}
          <div className="hidden md:block mt-10">
            <div className="overflow-hidden bg-white">
              {/* header row */}
              <div className="grid grid-cols-[220px_repeat(4,1fr)]">
                <div className="bg-white" />
                {plans.map((p, idx) => (
                  <div
                    key={p.titleKey}
                    className="bg-[#65CDBF] text-[#FFFFFF] text-center py-4 font-[600] uppercase text-[16px] xl:text-[20px]"
                  >
                    {t(p.titleKey)}
                  </div>
                ))}
              </div>

              {/* body */}
              {rows.map((row, rIdx) => (
                <div
                  key={row.labelKey}
                  className={`grid grid-cols-[220px_repeat(4,1fr)] ${
                    rIdx % 2 === 0 ? "bg-[#F0FAF9]" : "bg-white"
                  }`}
                >
                  <div className="py-4 px-5 text-[#000000B2] text-[14px] inter">
                    {t(row.labelKey)}
                  </div>

                  {row.values.map((vKey) => {
                    const isBold =
                      ["account.8", "account.9", "account.10", "account.11"].includes(vKey);

                    return (
                      <div
                        key={vKey}
                        className={`py-4 px-5 text-center inter
                          ${
                            isBold
                              ? "text-[16px] font-[600] text-[#000000B2]"
                              : "text-[14px] font-[500] text-[#000000B2]"
                          }
                        `}
                      >
                        {t(vKey)}
                      </div>
                    );
                  })}

                </div>
              ))}

              <div className="py-6 flex justify-center bg-white">
                <Button>{t("account.startNow")}</Button>
              </div>
            </div>
          </div>

          {/* MOBILE SWIPER */}
          <div className="md:hidden mt-8">
            <div className="flex items-center justify-between mb-4 absolute z-30 top-[15px] w-[calc(100%-40px)] mx-[20px]">
              <button
                className="swiper-btn-prev"
                aria-label="Previous"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="47" height="22" viewBox="0 0 47 22" fill="none">
                  <rect x="47" y="22" width="47" height="22" rx="2" transform="rotate(-180 47 22)" fill="white"/>
                  <path d="M11.6464 10.6464C11.4512 10.8417 11.4512 11.1583 11.6464 11.3536L14.8284 14.5355C15.0237 14.7308 15.3403 14.7308 15.5355 14.5355C15.7308 14.3403 15.7308 14.0237 15.5355 13.8284L12.7071 11L15.5355 8.17158C15.7308 7.97631 15.7308 7.65973 15.5355 7.46447C15.3403 7.26921 15.0237 7.26921 14.8284 7.46447L11.6464 10.6464ZM35 11L35 10.5L12 10.5L12 11L12 11.5L35 11.5L35 11Z" fill="#061A77"/>
                </svg>
              </button>

              <button
                className="swiper-btn-next"
                aria-label="Next"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="47" height="22" viewBox="0 0 47 22" fill="none">
                  <rect x="3.8466e-06" width="47" height="22" rx="2" fill="white"/>
                  <path d="M35.3536 11.3536C35.5488 11.1583 35.5488 10.8417 35.3536 10.6465L32.1716 7.46447C31.9763 7.26921 31.6597 7.26921 31.4645 7.46447C31.2692 7.65973 31.2692 7.97631 31.4645 8.17158L34.2929 11L31.4645 13.8284C31.2692 14.0237 31.2692 14.3403 31.4645 14.5355C31.6597 14.7308 31.9763 14.7308 32.1716 14.5355L35.3536 11.3536ZM12 11L12 11.5L35 11.5L35 11L35 10.5L12 10.5L12 11Z" fill="#061A77"/>
                </svg>
              </button>
            </div>
            <Swiper
              modules={[Navigation, A11y, Autoplay]}
              spaceBetween={14}
              slidesPerView={1}
              navigation={{
                prevEl: ".swiper-btn-prev",
                nextEl: ".swiper-btn-next",
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: true, // не выключать после свайпа/кликов
                pauseOnMouseEnter: true,     // пауза при наведении (по желанию)
              }}
            >
              {plans.map((plan, planIdx) => (
                <SwiperSlide key={plan.titleKey}>
                  <div className="rounded-[14px] overflow-hidden bg-white">
                    <div className="bg-[#41C0B2] text-white text-center py-4 font-semibold uppercase text-[12px] tracking-[0.12em]">
                      {t(plan.titleKey)}
                    </div>

                    <div className="p-4">
                      <div className="space-y-3">
                        {rows.map((row) => {  
                          const isBold =
                            ["account.8", "account.9", "account.10", "account.11"].includes(row.values[planIdx]);
                          return(
                          <div
                            key={row.labelKey}
                            className="flex items-start justify-between gap-4 pb-3"
                          >
                            <div className={`text-[#000000B2] inter text-[12px] font-medium `}>
                              {t(row.labelKey)}
                            </div>
                            <div className={`text-[#000000B2] ${isBold ? "font-[500] text-[15px]" : ""} inter text-[14px] text-right leading-[1.2] max-w-[62%]`}>
                              {t(row.values[planIdx])}
                            </div>
                          </div>
                        );})}
                      
                      </div>

                      <div className="mt-5 flex justify-center">
                        <Button/>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </Fade>
      </div>
    </div>
  );
}

export default Main;
